import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Code, Rocket, Wrench, Monitor } from 'lucide-react';
import { useFeaturedProjects, useSkills } from '../hooks/useProjects';
import type { Skill, Project } from '../types/database';
import LiveSitePreview, { shouldEmbedLiveUrl } from './portfolio/LiveSitePreview';

const TeaserCard: React.FC<{ project: Project }> = ({ project }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const inDevelopment = `${project.short_description || ''} ${project.description || ''}`
    .toLowerCase()
    .match(/in development|name is evolving/);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const showIframePreview =
    !project.image_url &&
    project.live_url &&
    isVisible &&
    shouldEmbedLiveUrl(project.live_url);

  return (
    <Link
      to={`/portfolio/${project.slug}`}
      className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
    >
      <div ref={cardRef} className="relative h-40 bg-gray-100 overflow-hidden">
        {project.image_url ? (
          <img
            src={project.image_url}
            alt={project.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : showIframePreview && project.live_url ? (
          <LiveSitePreview
            name={project.name}
            liveUrl={project.live_url}
            variant="card"
            className="w-full h-full"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)' }}
          >
            <div className="text-center z-10 px-3">
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-br from-[#0088cc]/20 to-[#4caf50]/20 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-white/40" />
              </div>
              <span className="text-xs text-white/50 line-clamp-1">{project.name}</span>
            </div>
          </div>
        )}

        <div className="absolute top-2 right-2 z-20">
          {project.live_url && !inDevelopment ? (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-[#4caf50]/90 backdrop-blur-sm text-white text-[10px] font-medium rounded-full">
              <Rocket className="w-2.5 h-2.5" />
              Live
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-[#0088cc]/80 backdrop-blur-sm text-white text-[10px] font-medium rounded-full">
              <Wrench className="w-2.5 h-2.5" />
              Dev
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-semibold text-lg mb-2 group-hover:text-[#0088cc] transition-colors">
          {project.name}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
          {project.short_description || project.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {project.technologies?.slice(0, 3).map((tech) => (
            <span
              key={tech}
              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};

const PortfolioTeaser: React.FC = () => {
  const { projects, loading: projectsLoading } = useFeaturedProjects();
  const { skills, loading: skillsLoading } = useSkills();

  // Get top skills from AI Tools category
  const aiTools = skills['AI Tools'] || [];
  const topSkills = aiTools.slice(0, 3);

  // Get featured projects (max 3)
  const featuredProjects = projects.slice(0, 3);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0088cc]/10 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#0088cc]" />
            <span className="text-sm font-medium text-[#0088cc]">Built with AI</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            See What I'm Building
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Real projects built with cutting-edge AI development tools.
            Fast, efficient, and production-ready.
          </p>
        </div>

        {/* Skills Badges */}
        {!skillsLoading && topSkills.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {topSkills.map((skill: Skill) => (
              <div
                key={skill.id}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100"
              >
                <Code className="w-4 h-4 text-[#0088cc]" />
                <span className="font-medium">{skill.name}</span>
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full capitalize">
                  {skill.level}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Featured Projects */}
        {projectsLoading ? (
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-40 bg-gray-200" />
                <div className="p-6">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : featuredProjects.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {featuredProjects.map((project) => (
              <TeaserCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 mb-12">
            <p className="text-gray-500">No featured projects yet. Check back soon!</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0088cc] text-white rounded-lg hover:bg-[#006699] transition-colors font-medium text-lg"
          >
            View Full Portfolio
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            Browse all projects, skills, and request code access
          </p>
        </div>
      </div>
    </section>
  );
};

export default PortfolioTeaser;
